import React from 'react';
import { BearingAdvancedFilterKey, BearingPayload, BearingTypeCode, TimkenUnit, UnitType } from '../../components/bearing-search/types/types';
import { getTooltip, handleActionError } from '../utils/engineering-tools';
import { bearingSearchLabels, text } from '../data';
import { convertNumberInput, isValidBoundary } from '../utils/validations';

export type BearingSearchState = {
	unit: TimkenUnit;
	marketPlaceDescription: string;
	partType: string[];
	bearingType: string[];
	bearingSubType: string[];
	advancedFilters: BearingAdvancedFilterMap;
	validation: BearingAdvancedFilterValidationMap;
	requestData?: Partial<BearingPayload>;
};

export type BearingAdvancedFilter = {
	id: string;
	label: string;
	value?: number;
	type: UnitType;
	tooltip?: string;
	isApplicable?: boolean;
};

type BearingAdvancedFilterMap = { [Property in BearingAdvancedFilterKey]: BearingAdvancedFilter };
type BearingAdvancedFilterValidationMap = Map<BearingAdvancedFilterKey, { invalid: boolean, message: string; }>;

const defaultFilterValue: Pick<BearingAdvancedFilter, 'value' | 'type' | 'isApplicable'> = {
	type: 'length',
	isApplicable: true,
};

const filterPairs: Array<[BearingAdvancedFilterKey, BearingAdvancedFilterKey]> = [
	[ 'boreMinimum', 'boreMaximum' ],
	[ 'outerDiameterMinimum', 'outerDiameterMaximum' ],
	[ 'bearingWidthMinimum', 'bearingWidthMaximum' ],
	[ 'c1RatingMinimum', 'c1RatingMaximum' ],
	[ 'c90RatingMinimum', 'c90RatingMaximum' ],
	[ 'ceRatingMinimum', 'ceRatingMaximum' ],
	[ 'kFactorMinimum', 'kFactorMaximum' ],
	[ 'y1FactorMinimum', 'y1FactorMaximum' ],
];

const getValidationMap = ( state?: BearingSearchState ): BearingAdvancedFilterValidationMap => {
	if ( ! state ) {
		// assign initial value
		const initialValidationMap: BearingAdvancedFilterValidationMap = new Map();

		filterPairs.forEach( ( [ min, max ] ) => {
			initialValidationMap.set( min, {
				message: text.errorBoundaryLower,
				invalid: false,
			} );

			initialValidationMap.set( max, {
				message: text.errorBoundaryUpper,
				invalid: false,
			} );
		} );

		return initialValidationMap;
	}

	const { advancedFilters: filters, validation: map } = state;

	const validation = new Map(Object.entries(map || {}));

	filterPairs.forEach( ( [ min, max ] ) => {
		const invalid = ! isValidBoundary( filters[ min ].value, filters[ max ].value );

		validation.set( min, {
			invalid,
			message: text.errorBoundaryLower,
		} );
		validation.set( max, {
			invalid,
			message: text.errorBoundaryUpper,
		} );
	} );

	return validation;
};

const initialAdvancedFilters: BearingAdvancedFilterMap = {
	boreMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_bore_min',
		label: bearingSearchLabels.boreMinimum || '',
		tooltip: bearingSearchLabels.boreMinimum && getTooltip( bearingSearchLabels.boreMinimum ),
	},
	boreMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_bore_max',
		label: bearingSearchLabels.boreMaximum || '',
		tooltip: bearingSearchLabels.boreMaximum && getTooltip( bearingSearchLabels.boreMaximum ),
	},
	outerDiameterMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_out_diameter_min',
		label: bearingSearchLabels.outerDiameterMinimum || '',
		tooltip: bearingSearchLabels.outerDiameterMinimum && getTooltip( bearingSearchLabels.outerDiameterMinimum ),
	},
	outerDiameterMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_out_diameter_max',
		label: bearingSearchLabels.outerDiameterMaximum || '',
		tooltip: bearingSearchLabels.outerDiameterMaximum && getTooltip( bearingSearchLabels.outerDiameterMaximum ),
	},
	bearingWidthMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_width_min',
		label: bearingSearchLabels.bearingWidthMinimum || '',
		tooltip: bearingSearchLabels.bearingWidthMinimum && getTooltip( bearingSearchLabels.bearingWidthMinimum ),
	},
	bearingWidthMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_width_max',
		label: bearingSearchLabels.bearingWidthMaximum || '',
		tooltip: bearingSearchLabels.bearingWidthMaximum && getTooltip( bearingSearchLabels.bearingWidthMaximum ),
	},
	c1RatingMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_c1_min',
		type: 'force',
		label: bearingSearchLabels.c1RatingMinimum || '',
		tooltip: bearingSearchLabels.c1RatingMinimum && getTooltip( bearingSearchLabels.c1RatingMinimum ),
	},
	c1RatingMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_c1_max',
		type: 'force',
		label: bearingSearchLabels.c1RatingMaximum || '',
		tooltip: bearingSearchLabels.c1RatingMaximum && getTooltip( bearingSearchLabels.c1RatingMaximum ),
	},
	c90RatingMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_c90_min',
		type: 'force',
		label: bearingSearchLabels.c90RatingMinimum || '',
		tooltip: bearingSearchLabels.c90RatingMinimum && getTooltip( bearingSearchLabels.c90RatingMinimum ),
	},
	c90RatingMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_c90_max',
		type: 'force',
		label: bearingSearchLabels.c90RatingMaximum || '',
		tooltip: bearingSearchLabels.c90RatingMaximum && getTooltip( bearingSearchLabels.c90RatingMaximum ),
	},
	ceRatingMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_ce_min',
		type: 'force',
		label: bearingSearchLabels.ceRatingMinimum || '',
		tooltip: bearingSearchLabels.ceRatingMinimum && getTooltip( bearingSearchLabels.ceRatingMinimum ),
	},
	ceRatingMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_ce_max',
		type: 'force',
		label: bearingSearchLabels.ceRatingMaximum || '',
		tooltip: bearingSearchLabels.ceRatingMaximum && getTooltip( bearingSearchLabels.ceRatingMaximum ),
	},
	kFactorMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_k_factor_min',
		type: 'unitless',
		label: bearingSearchLabels.kFactorMinimum || '',
		tooltip: bearingSearchLabels.kFactorMinimum && getTooltip( bearingSearchLabels.kFactorMinimum ),
	},
	kFactorMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_k_factor_max',
		type: 'unitless',
		label: bearingSearchLabels.kFactorMaximum || '',
		tooltip: bearingSearchLabels.kFactorMaximum && getTooltip( bearingSearchLabels.kFactorMaximum ),
	},
	y1FactorMinimum: {
		...defaultFilterValue,
		id: 'bearing_search_y1_factor_min',
		type: 'unitless',
		label: bearingSearchLabels.y1FactorMinimum || '',
		tooltip: bearingSearchLabels.y1FactorMinimum && getTooltip( bearingSearchLabels.y1FactorMinimum ),
	},
	y1FactorMaximum: {
		...defaultFilterValue,
		id: 'bearing_search_y1_factor_max',
		type: 'unitless',
		label: bearingSearchLabels.y1FactorMaximum || '',
		tooltip: bearingSearchLabels.y1FactorMaximum && getTooltip( bearingSearchLabels.y1FactorMaximum ),
	},
};

export const initialBearingSearchState: BearingSearchState = {
	unit: 'M',
	marketPlaceDescription: '',
	partType: [],
	bearingType: [],
	bearingSubType: [],
	advancedFilters: initialAdvancedFilters,
	validation: getValidationMap(),
};

const updateAdvancedFilters = ( initialFilters: BearingAdvancedFilterMap, bearingType: string[] ): BearingAdvancedFilterMap => {
	const filters = { ...initialFilters };
	Object.values( filters ).forEach( ( filter ) => filter.isApplicable = true );
	const hiddenFields: BearingAdvancedFilterKey[] = [];

	if ( bearingType.length > 0 ) {
		if ( ! bearingType.filter( ( type ) => type !== 'RBB' && type !== 'ACBB' ).length ) {
			hiddenFields.push( 'c1RatingMinimum', 'c1RatingMaximum' );
		}

		if ( ! bearingType.includes( BearingTypeCode.TRB ) ) {
			hiddenFields.push( 'c90RatingMinimum', 'c90RatingMaximum', 'kFactorMinimum', 'kFactorMaximum' );
		}

		if ( ! bearingType.includes( BearingTypeCode.SRB ) ) {
			hiddenFields.push( 'y1FactorMinimum', 'y1FactorMaximum' );
		}

		if ( ! bearingType.includes( BearingTypeCode.RBB ) && ! bearingType.includes( BearingTypeCode.ACBB ) ) {
			hiddenFields.push( 'ceRatingMinimum', 'ceRatingMaximum' );
		}

		for ( const key of hiddenFields ) {
			filters[ key ].isApplicable = false;
		}
	}

	return filters;
};

export type BearingSearchAction =
	| { type: 'change_unit'; payload: TimkenUnit; }
	| { type: 'change_part_number'; payload: string; }
	| {
		type: 'change_bearing_type'; payload: {
			partType: string[];
			bearingType: string[];
			bearingSubType: string[];
		};
	}
	| { type: 'change_advanced_filter'; payload: Array<keyof BearingSearchState>; }
	| { type: 'change_number_field'; payload: [keyof BearingPayload, unknown]; }
	| { type: 'update_request_data'; }
	| { type: 'reset'; payload?: BearingSearchState; };

const bearingSearchReducer: React.Reducer<
	BearingSearchState,
	BearingSearchAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			const unit = action.payload;

			return {
				...state,
				unit,
			};
		}
		case 'change_part_number': {
			return {
				...state,
				marketPlaceDescription: action.payload,
			};
		}
		case 'change_bearing_type': {
			return {
				...state,
				...action.payload,
				advancedFilters: updateAdvancedFilters( state.advancedFilters, action.payload.bearingType ),
			};
		}
		case 'change_advanced_filter': {
			return {
				...state,
				advancedFields: [ ...action.payload ],
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			if ( ! state.advancedFilters[ key ] ) {
				return { ...state };
			}

			const filter = { ...state.advancedFilters[ key ] };

			return {
				...state,
				advancedFilters: {
					...state.advancedFilters,
					[ key ]: {
						...filter,
						value: convertNumberInput( value ),
					},
				},
			};
		}
		case 'update_request_data': {
			const validation = getValidationMap( state );
			const isInValid = Array.from( validation.values() ).some( ( field ) => field.invalid );

			if ( isInValid ) {
				return {
					...state,
					validation,
					requestData: undefined,
				};
			}

			const { unit, marketPlaceDescription, partType, advancedFilters } = state;
			const requestData: Partial<BearingPayload> = { unit, marketPlaceDescription, partType };

			Object.keys( advancedFilters ).filter( ( key ) => {
				const field = advancedFilters[ key ] as BearingAdvancedFilter;

				return field.isApplicable && field.value;
			} ).forEach( ( key ) => {
				const field = advancedFilters[ key ] as BearingAdvancedFilter;

				requestData[ key ] = field.value;
			} );

			return {
				...state,
				validation,
				requestData,
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialBearingSearchState;

			return {
				...newState,
			};
		}
	}

	return handleActionError( action );
};

export default bearingSearchReducer;
